import { useCallback, useEffect } from 'react';
import { useImmerAtom, useSetImmerAtom } from 'jotai-immer';
import { tblSpecLoadStorage, tblSpecLoadView } from '../../../../core/table/tableViewsRepo';
import {
    tableDataVM,
    tableFiltersVM,
    tableStateVM,
    tableStatsVM,
    tableViewVM,
} from '../../../../core/table/table_view_models';
import { VENDOR_LIST_STORAGE_KEY, VENDOR_LIST_VIEW_KEY } from '../../vendor_const';
import {
    tableStateAtom,
    tableViewAtom,
    tableStatsAtom,
    tableFiltersAtom,
    setAtomVendorListTableViewState,
    vendorDataAtom,
} from '../vendor_list_atoms';
import viewStateRepo from '../../../../core/repo/viewStateRepo';
import { RawVendorDataItem, VendorDataItem } from '../vendor_list_types';
import { ListTableInitHookReturn } from '../../../../core/table/table.types';
import apiSvc from '../../../../core/services/apiSvc';
import { vendorListColumnSpec, vendorListDefaultView } from './VendorListTableSpec';
import { GET_LIST } from '../../data/vendorRepo';

export const useVendorListTableInit = (): ListTableInitHookReturn => {
    const [{ init }, setState] = useImmerAtom(tableStateAtom);
    const setView = useSetImmerAtom(tableViewAtom);
    const setList = useSetImmerAtom(vendorDataAtom);
    const setStats = useSetImmerAtom(tableStatsAtom);
    const setFilter = useSetImmerAtom(tableFiltersAtom);

    const getListAsync = useCallback(async () => {
        try {
            const r = await apiSvc.getEntityList<RawVendorDataItem>(GET_LIST, {});
            setList(draft => {
                draft.dataItems = vendorListVM(r);
                draft.items = [];
            });
        } catch (err: any) {
            // err msg handled upstream
            setList(() => tableDataVM<VendorDataItem>());
        } finally {
            setState(draft => {
                draft.waiting = false;
                draft.init = false;
                return draft;
            });
        }
    }, [setList, setState]);

    // INIT
    useEffect(() => {
        setState(draft => {
            if (draft.init && !draft.waiting) {
                // load custom views from storage
                const colSpec = vendorListColumnSpec();
                const defaultView = vendorListDefaultView();
                const viewStorage = tblSpecLoadStorage(VENDOR_LIST_VIEW_KEY, colSpec, defaultView);
                draft.views = viewStorage.views;
                draft.currentViewId = viewStorage.current;

                // set current view
                setView(() => tblSpecLoadView(viewStorage.current, viewStorage.views, colSpec) || tableViewVM());

                // viewstate filters and paging
                const viewstate = viewStateRepo.getTable(VENDOR_LIST_STORAGE_KEY, Array.from(colSpec.keys()));
                if (!!viewstate) {
                    setView(draft => {
                        draft.sort = viewstate.sort;
                    });
                    setFilter(() => viewstate.flt);
                    setStats(draft => {
                        draft.paging = viewstate.paging;
                    });
                }

                getListAsync();

                draft.waiting = true;
                return draft;
            } else {
                return draft;
            }
        });

        // destroy
        return (): void => {
            setAtomVendorListTableViewState();
            setState(() => tableStateVM());
            setView(() => tableViewVM());
            setFilter(() => tableFiltersVM());
            setStats(() => tableStatsVM());
            setList(() => tableDataVM<VendorDataItem>());
        };
    }, [setState, setView, getListAsync, setList, setFilter, setStats]);

    return { init };
};

function vendorListVM(data: RawVendorDataItem[]): VendorDataItem[] {
    const items = Array.isArray(data) ? data : [];
    const nextItems: any[] = [];
    /**
     * Doing some weird data massaging here to convert the SupportsEDI field
     * from a boolean to a string for display purposes.
     * This is a temporary solution until the backend can be updated to return
     * the correct type.
     */

    items.forEach(item => {
        const supportsEDI = item.SupportsEDI === true ? 'YES' : 'NO';
        nextItems.push({
            ...item,
            SupportsEDI: supportsEDI,
        } as VendorDataItem);
    });

    return nextItems;
}
